import type { ProductListingQueryState } from "./product-listing-query-state";

const PRODUCT_LISTING_PAGINATION_PREFIX = "product-listing-pagination";
export const DEFAULT_PRODUCT_LISTING_PAGE_SIZE = 20;

type SessionStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

type StoredPaginationState = {
  currentPage: number;
  querySignature: string;
};

export function getProductListingPaginationSessionKey(
  listingId: string
): string {
  return `${PRODUCT_LISTING_PAGINATION_PREFIX}:${listingId}`;
}

export function getProductListingQuerySignature(
  queryState: ProductListingQueryState,
  pageSize: number | undefined
): string {
  return JSON.stringify({
    filters: queryState.filters ?? "",
    pageSize: pageSize ?? null,
    sort: queryState.sort ?? "",
  });
}

export function readProductListingPaginationState(
  storage: SessionStorage,
  listingId: string,
  querySignature: string
): number {
  const key = getProductListingPaginationSessionKey(listingId);

  try {
    const storedValue = storage.getItem(key);
    if (!storedValue) {
      return 0;
    }

    const stored = JSON.parse(storedValue) as Partial<StoredPaginationState>;
    if (
      stored.querySignature !== querySignature ||
      !Number.isInteger(stored.currentPage) ||
      (stored.currentPage ?? 0) < 0
    ) {
      storage.removeItem(key);
      return 0;
    }

    return stored.currentPage ?? 0;
  } catch {
    clearProductListingPaginationState(storage, listingId);
    return 0;
  }
}

export function writeProductListingPaginationState(
  storage: SessionStorage,
  listingId: string,
  currentPage: number,
  querySignature: string
): void {
  try {
    storage.setItem(
      getProductListingPaginationSessionKey(listingId),
      JSON.stringify({ currentPage, querySignature })
    );
  } catch {
    // Pagination still works when session storage is unavailable.
  }
}

export function clearProductListingPaginationState(
  storage: SessionStorage,
  listingId: string
): void {
  try {
    storage.removeItem(getProductListingPaginationSessionKey(listingId));
  } catch {
    // Pagination still works when session storage is unavailable.
  }
}
